import { DragDropContext, Draggable, Droppable, DropResult } from '@hello-pangea/dnd';
import { alpha, Box, CircularProgress, Paper, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTodos } from '../../context/TodoContext/TodoContext';
import TodoDetail from '../TodoDetail/TodoDetail';
import TodoInput from '../TodoList/components/TodoInput';
import TodoItemContent from '../TodoList/components/TodoItemContent/TodoItemContent';
import ListHeader from './components/ListHeader';
import Loading from '../../../../context/Loading/Loading';

const TodoListMulti = () => {
    const {
        todosMap,
        addTodo,
        lists,
        addList,
        deleteList,
        reorderList,
        moveTodoToList,
        updateListTitle,
        activeWorkspace,
        setActiveWorkspace
    } = useTodos();

    const theme = useTheme();
    const navigate = useNavigate();
    const scrollContainerRef = useRef<HTMLDivElement | null>(null);

    const { workspaceId: paramWorkspaceId, id: selectedTodoId } = useParams();
    const [workspaceId, setWorkspaceId] = useState<string | null>(null);

    useEffect(() => {
        if (paramWorkspaceId) {
            setActiveWorkspace(paramWorkspaceId);
            setWorkspaceId(paramWorkspaceId);
        }
        else
            navigate(`/todo/${activeWorkspace}`, { replace: true });
    }, [workspaceId, navigate, activeWorkspace, paramWorkspaceId, setActiveWorkspace]);

    const handleClose = () => {
        navigate(`/todo/${workspaceId}`, { replace: true });
    };

    const handleDragEnd = (result: DropResult) => {
        const { source, destination, draggableId, type } = result;

        if (!destination) return;

        if (type === 'list') {
            reorderList(source.index, destination.index);
            return;
        }

        if (source.droppableId === destination.droppableId) {
            moveTodoToList(draggableId, source.droppableId, destination.droppableId, destination.index);
        } else {
            moveTodoToList(draggableId, source.droppableId, destination.droppableId, destination.index);
        }
    };

    const handleAddTodo = (text: string, listId: string) => {
        addTodo(text, listId);
    };

    const handleAddList = () => {
        addList();
        setTimeout(() => {
            if (scrollContainerRef.current) {
                scrollContainerRef.current.scrollTo({
                    left: scrollContainerRef.current.scrollWidth,
                    behavior: 'smooth'
                });
            }
        }, 0);
    };

    const handleUpdateListTitle = (listId: string, newTitle: string) => {
        console.log(listId, newTitle);
        updateListTitle(listId, newTitle);
    };

    const EmptyListMessage = () => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                p: 2,
                height: '100%',
                minHeight: 100,
            }}
        >
            <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                    textAlign: 'center',
                    fontStyle: 'italic'
                }}
            >
                No tasks yet. Add a new task to get started!
            </Typography>
        </Box>
    );

    if (!workspaceId) return <CircularProgress />;

    return (
        <Stack direction='row' height='100%' width='100dvw' overflow='hidden'>
            <DragDropContext onDragEnd={handleDragEnd}>
                <Droppable droppableId="all-lists" direction="horizontal" type="list">
                    {(provided) => (
                        <Box
                            ref={(el: HTMLDivElement) => {
                                scrollContainerRef.current = el;
                                provided.innerRef(el);
                            }}
                            // ref={provided.innerRef}
                            {...provided.droppableProps}
                            sx={{
                                display: 'flex',
                                gap: 2,
                                p: 2,
                                overflowX: 'auto',
                                flex: 1,
                            }}
                        >
                            {lists.map((list, index) => {
                                if (!list?.id) return null;

                                return (
                                    <Draggable
                                        key={list.id}
                                        draggableId={`list-${list.id}`}
                                        index={index}
                                    >
                                        {(provided, snapshot) => (
                                            <Stack
                                                ref={provided.innerRef}
                                                {...provided.draggableProps}
                                                {...provided.dragHandleProps}
                                                spacing={2}
                                                sx={{
                                                    minWidth: 380,
                                                    height: '100%',
                                                    [theme.breakpoints.down('sm')]: {
                                                        minWidth: '100%',
                                                    },
                                                    transform: snapshot.isDragging ? `${provided.draggableProps.style?.transform} rotate(2deg)` : provided.draggableProps.style?.transform,
                                                }}
                                            >
                                                <ListHeader
                                                    title={list.title}
                                                    onTitleChange={(newTitle) => handleUpdateListTitle(list.id, newTitle)}
                                                    onDelete={() => deleteList(list.id)}
                                                />

                                                <TodoInput onAdd={(text) => handleAddTodo(text, list.id)} />

                                                <Droppable droppableId={list.id} type="todo">
                                                    {(provided) => (
                                                        <Paper
                                                            ref={provided.innerRef}
                                                            {...provided.droppableProps}
                                                            sx={{
                                                                flex: 1,
                                                                minHeight: 100,
                                                                overflowY: 'auto',
                                                                bgcolor: theme => alpha(theme.palette.background.paper, 0.5),
                                                            }}
                                                        >
                                                            <Stack spacing={1} sx={{ p: 1 }}>
                                                                {list.todos.length === 0 ? (
                                                                    <EmptyListMessage />
                                                                ) : (
                                                                    list.todos.map((todoId, index) => {
                                                                        const todo = todosMap.get(todoId);
                                                                        if (!todo) return null;

                                                                        return (
                                                                            <Draggable
                                                                                key={todoId}
                                                                                draggableId={todoId}
                                                                                index={index}
                                                                            >
                                                                                {(provided) => (
                                                                                    <div
                                                                                        ref={provided.innerRef}
                                                                                        {...provided.draggableProps}
                                                                                        {...provided.dragHandleProps}
                                                                                    >
                                                                                        <TodoItemContent id={todoId} workspaceId={workspaceId} />
                                                                                    </div>
                                                                                )}
                                                                            </Draggable>
                                                                        );
                                                                    })
                                                                )}
                                                                {provided.placeholder}
                                                            </Stack>
                                                        </Paper>
                                                    )}
                                                </Droppable>
                                            </Stack>
                                        )}
                                    </Draggable>
                                );
                            })}
                            {provided.placeholder}
                            <Stack
                                spacing={2}
                                sx={{
                                    minWidth: 380,
                                    height: '100%',
                                    [theme.breakpoints.down('sm')]: {
                                        minWidth: '100%',
                                    },
                                    opacity: 0.8,
                                    transition: 'opacity 0.2s',
                                    '&:hover': {
                                        opacity: 1,
                                    }
                                }}
                            >
                                <Paper
                                    onClick={handleAddList}
                                    sx={{
                                        height: '100%',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        bgcolor: theme => alpha(theme.palette.background.paper, 0.5),
                                        border: '2px dashed',
                                        borderColor: 'divider',
                                        '&:hover': {
                                            borderColor: 'primary.main',
                                            bgcolor: theme => alpha(theme.palette.primary.main, 0.05),
                                        }
                                    }}
                                >
                                    <Typography variant='body1' color='textSecondary'>Add List</Typography>
                                </Paper>
                            </Stack>
                        </Box>
                    )}

                </Droppable>
            </DragDropContext>


            {selectedTodoId && (
                <TodoDetail
                    id={selectedTodoId}
                    onClose={handleClose}
                    sticky
                />
            )}
        </Stack>
    );
};

export default TodoListMulti;